/** External Imports */
import { useEffect, useState } from "react";

/** Internal Imports */
import CSS from "./ScheduleNotification.module.css";
import CardContainer from "@/Component/Elements/CardContainer/CardContainer";
import CalendarIcon from "@/Component/Elements/Icon/CalendarIcon/CalendarIcon";
import { ReactComponent as ScheduleNoDataImage } from "./img/ScheduleNoData.svg";
import { ReactComponent as TimeImage } from "./img/Time.svg";

type t_ScheduleTab = "after" | "before";
type t_ScheduleNotificationProps = {
    scheduleAfterList: Record<string, any>[];
    scheduleBeforeList: Record<string, any>[];
};

const SCHEDULE_PREVIEW_COUNT = 2;





const ScheduleNotification = (iProps: t_ScheduleNotificationProps) => {
    const {
        scheduleAfterList,
        scheduleBeforeList,
    } = iProps;

    const [activeScheduleTab, setActiveScheduleTab] = useState<t_ScheduleTab>("after");
    const [isScheduleExpanded, setIsScheduleExpanded] = useState<boolean>(false);

    useEffect(() => {
        setIsScheduleExpanded(false);
    }, [activeScheduleTab]);

    const activeScheduleList = activeScheduleTab === "after"
        ? scheduleAfterList
        : scheduleBeforeList;
    const visibleScheduleList = isScheduleExpanded
        ? activeScheduleList
        : activeScheduleList.slice(0, SCHEDULE_PREVIEW_COUNT);
    const hiddenScheduleCount = Math.max(activeScheduleList.length - SCHEDULE_PREVIEW_COUNT, 0);

    return (
        <CardContainer class_name_list={[CSS.ScheduleNotification]}>
            <div className={CSS.ScheduleHeader}>
                <div className={CSS.ScheduleHeaderTitleWrap}>
                    <span className={CSS.ScheduleHeaderIcon} aria-hidden="true">
                        <CalendarIcon />
                    </span>
                    <h3 className={CSS.ScheduleHeaderTitle}>
                        나의 스케줄
                    </h3>
                </div>

                <div className={CSS.ScheduleTabs} role="tablist" aria-label="스케줄 구분">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeScheduleTab === "after"}
                        className={[
                            CSS.ScheduleTabButton,
                            activeScheduleTab === "after" && CSS.IsActive
                        ].filter(Boolean).join(" ")}
                        onClick={() => {
                            setActiveScheduleTab("after");
                        }}
                    >
                        향후 일정
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeScheduleTab === "before"}
                        className={[
                            CSS.ScheduleTabButton,
                            activeScheduleTab === "before" && CSS.IsActive
                        ].filter(Boolean).join(" ")}
                        onClick={() => {
                            setActiveScheduleTab("before");
                        }}
                    >
                        지난 일정
                    </button>
                </div>
            </div>

            {activeScheduleList.length > 0 ? (
                <>
                    <ul
                        className={[
                            CSS.ScheduleList,
                            activeScheduleTab === "before" && CSS.ScheduleListPast
                        ].filter(Boolean).join(" ")}
                    >
                        {visibleScheduleList.map((iRow: Record<string, any>, index: number) => (
                            <li
                                className={[
                                    CSS.ScheduleListItem,
                                    activeScheduleTab === "after" && index === 0 && CSS.ScheduleListItemPrimary
                                ].filter(Boolean).join(" ")}
                                key={iRow.schedule_id || index}
                            >
                                <div className={CSS.ScheduleItemHeader}>
                                    <span className={CSS.ScheduleBadge}>
                                        {DDayDisplay(iRow.d_day_num)}
                                    </span>
                                    <strong className={CSS.ScheduleItemTitle}>
                                        {getScheduleDisplayTitle(iRow)}
                                    </strong>
                                </div>
                                <div className={CSS.ScheduleMetaList}>
                                    <span className={CSS.ScheduleMetaItem}>
                                        <TimeImage />
                                        {getScheduleDateTimeText(iRow)}
                                    </span>
                                    {`${iRow.status_content || ""}`.trim() && (
                                        <>
                                            <span className={CSS.ScheduleMetaDivider} aria-hidden="true" />
                                            <span className={[CSS.ScheduleMetaItem, CSS.ScheduleMetaStatus].join(" ")}>
                                                {iRow.status_content}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>

                    {(!isScheduleExpanded && hiddenScheduleCount > 0) && (
                        <button
                            type="button"
                            className={CSS.ScheduleMoreButton}
                            onClick={() => {
                                setIsScheduleExpanded(true);
                            }}
                        >
                            {hiddenScheduleCount}개의 일정 더 보기
                        </button>
                    )}
                </>
            ) : (
                <div className={CSS.ScheduleEmptyState}>
                    <div className={CSS.ScheduleEmptyStateIconWrap}>
                        <ScheduleNoDataImage
                            className={CSS.ScheduleEmptyStateIcon}
                            aria-hidden="true"
                            focusable="false"
                        />
                    </div>

                    <strong className={CSS.ScheduleEmptyStateTitle}>
                        {activeScheduleTab === "after"
                            ? "아직 예정된 일정이 없습니다"
                            : "아직 지난 일정이 없습니다"}
                    </strong>
                    <p className={CSS.ScheduleEmptyStateDescription}>
                        {activeScheduleTab === "after"
                            ? "스튜디오 담당자가 일정을 확정하면 이곳에 표시됩니다. 조금만 기다려주세요!"
                            : "완료된 일정이 생기면 이곳에서 지난 스케줄을 확인할 수 있습니다."}
                    </p>
                </div>
            )}
        </CardContainer>
    );
};

export default ScheduleNotification;

function DDayDisplay(iDayNum: number | string) {
    const m_day_num = Number(iDayNum);

    if (Number.isNaN(m_day_num)) {
        return "-";
    }

    if (m_day_num > 0) {
        return `D-${m_day_num}`;
    }

    if (m_day_num < 0) {
        return `${Math.abs(m_day_num)}일 지남`;
    }

    return "Today";
}

function getScheduleDisplayTitle(iRow: Record<string, any>) {
    return `${iRow.detail?.package_name || iRow.package_name || "-"}`;
}

function getScheduleDateTimeText(iRow: Record<string, any>) {
    const dateText = `${iRow.detail?.schedule_date || iRow.schedule_date || ""}`.trim();
    const startText = `${iRow.detail?.photo_start || ""}`.trim();

    return [dateText, startText].filter(Boolean).join(" ");
}
